import { Button, ExternalLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useChecklistItemCtx } from '../../../../components/checklist';
import { StepWithPreview } from '../../shared/components/step-with-preview';
import { CustomerForm } from '../../shared/customer/customer-form';
import { CustomerGraphic } from '../../shared/customer/customer-graphic';
import type { OnboardingChecklistStep } from '../../shared/types';
import { useChecklistStatus } from './use-checklist-status';
import { useStoredCustomerEmail } from './use-stored-customer-email';
import { useTrackedCustomer } from './use-tracked-customer';

export const CUSTOMER_STEP: OnboardingChecklistStep = {
  icon: 'userAdd',
  id: 'customer',
  title: t('features:onboarding.components.customer_item.title'),
  useStatus: useChecklistStatus,
  children: <CreateCustomerBody />,
};

const CUSTOMER_GROUP_DOCS_URL = 'https://docs.sparklayer.io/customers';

function CreateCustomerBody() {
  const { status } = useChecklistItemCtx();
  // "Edit customer" reveals the form without clearing the stored email, so the existing customer survives until a new one is created.
  const [editing, setEditing] = useState<boolean>(false);

  return (
    <StepWithPreview preview={<CustomerGraphic />}>
      {status === 'complete' && !editing ? (
        <CreateCustomerComplete onEdit={() => setEditing(true)} />
      ) : (
        <CreateCustomerEdit onDone={() => setEditing(false)} />
      )}
    </StepWithPreview>
  );
}

// The form plus, when re-editing a completed step, a way to back out keeping the current customer.
function CreateCustomerEdit({ onDone }: { onDone: () => void }) {
  const { status } = useChecklistItemCtx();
  const { email: storedEmail, setEmail: setStoredEmail } = useStoredCustomerEmail();

  return (
    <>
      <Text>{t('features:onboarding.components.customer_item.description')}</Text>
      <CustomerForm
        submitLabel={t('features:onboarding.components.customer_item.submit')}
        onSuccess={(email) => {
          // Track the created (or adopted) customer; the tutorial's later steps key off this email.
          setStoredEmail(email);
          onDone();
        }}
      />
      {status === 'complete' && storedEmail && (
        <div>
          <Button variant="bare" onClick={onDone}>
            {t('features:onboarding.components.customer_item.keep_existing', {
              email: storedEmail,
            })}
          </Button>
        </div>
      )}
    </>
  );
}

function CreateCustomerComplete({ onEdit }: { onEdit: () => void }) {
  const { customer } = useTrackedCustomer();
  const email = customer?.email;

  return (
    <>
      <Text>
        {email
          ? t('features:onboarding.components.customer_item.complete_with_email', { email })
          : t('features:onboarding.components.customer_item.complete')}{' '}
        <ExternalLink link={CUSTOMER_GROUP_DOCS_URL} showIcon>
          {t('features:onboarding.components.customer_item.learn_more')}
        </ExternalLink>
      </Text>
      <div>
        <Button variant="secondary" icon="edit" onClick={onEdit}>
          {t('features:onboarding.components.customer_item.edit')}
        </Button>
      </div>
    </>
  );
}
